import * as React from 'react';
import {
  ToggleButton,
  ToggleButtonGroup,
  ToggleButtonGroupProps,
  Typography,
} from '@mui/material';
import { motion } from 'framer-motion';

export interface ToggleSwitchOption {
  /**
   * The label of the toggle switch option
   */
  label: string;
  /**
   * The value of the toggle switch option
   */
  value: string;
}

export interface ToggleSwitchProps
  extends Omit<ToggleButtonGroupProps, 'onChange'> {
  /**
   * The options of the toggle switch
   */
  options: [ToggleSwitchOption, ToggleSwitchOption];
  /**
   * The value of the toggle switch
   */
  value: string;
  /**
   * The onChange callback
   */
  onChange: (newValue: string) => void;
  /**
   * Whether to enable full control toggle
   */
  fullControlToggle?: boolean;
}

export function ToggleSwitch({
  options,
  value,
  onChange,
  fullControlToggle = false,
  className,
  ...rest
}: ToggleSwitchProps) {
  const handleToggleChange = (
    event: React.MouseEvent<HTMLElement>,
    newValue: string
  ) => {
    if (newValue !== null && newValue !== value) {
      onChange(newValue);
      return;
    }

    if (fullControlToggle && newValue === value) {
      const currentIndex = options.findIndex(option => option.value === value);
      const nextIndex = (currentIndex + 1) % options.length;
      const nextValue = options[nextIndex].value;

      onChange(nextValue);
    }
  };

  return (
    <ToggleButtonGroup
      className={`toggle-switch ${className || ''}`}
      exclusive
      value={value}
      onChange={handleToggleChange}
      aria-label="Selection switch"
      {...rest}
    >
      {(options ?? []).map(option => (
        <ToggleButton
          key={option.value}
          value={option.value}
          selected={option.value === value}
          disableRipple
        >
          {option.value === value && (
            <motion.div
              layout
              layoutId="toggle-pill"
              className="motion-toggle-pill"
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'white',
                borderRadius: '9999px',
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                zIndex: -1,
              }}
              transition={{
                type: 'spring',
                stiffness: 600,
                damping: 35,
              }}
            />
          )}

          <Typography variant="labelLarge">{option.label}</Typography>
        </ToggleButton>
      ))}
    </ToggleButtonGroup>
  );
}
